import React, { useState, useEffect } from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import Cursor from '../SVG/Cursor';

interface ShowTypingProps {
  msg: string;
  txt: string;
  pic: string;
  vid: string;
  custom?: () => JSX.Element;
  renderCustom?: boolean;
  loading?: boolean;
  resumeTyping?: boolean;
  setIsTyping?: (isTyping: boolean) => void;
}

const ShowTyping: React.FC<ShowTypingProps> = ({ msg , txt, pic, vid, custom, renderCustom, loading, setIsTyping, resumeTyping }) => {  
  const [displayResponse, setDisplayResponse] = useState("");
  const [completedTyping, setCompletedTyping] = useState(false);
  const contentRef = React.useRef(null);
  const appendedElementRef = React.useRef(null);


  useEffect(() => {
    if (contentRef.current) {
      
      if (appendedElementRef.current) {
        (appendedElementRef.current as any).remove();
      }

      if(!completedTyping) {
        const lastElement = getDeepestLastChild(contentRef.current);
        if (lastElement) {
          const cursor = document.createElement('span');
          cursor.style.color = 'white';
          cursor.style.fontWeight = '1000';
          cursor.style.paddingLeft = '10px';
          cursor.textContent = '|';

          (appendedElementRef.current as any) = cursor;
          lastElement.appendChild(cursor);
        }  
      }
    }
  }, [displayResponse, completedTyping]);

  useEffect(() => {
    setCompletedTyping(false);
    setIsTyping?.(true);

    let i = 0;
    const stringResponse = msg + txt;
    
    const intervalId = setInterval(() => {
      if (resumeTyping) {
        clearInterval(intervalId);
        setDisplayResponse(stringResponse);
        setCompletedTyping(true);
        setIsTyping?.(false);
        return;
      }

      setDisplayResponse(stringResponse.slice(0, i));
      i++;

      if (i > stringResponse.length) {
        clearInterval(intervalId);
        setCompletedTyping(true);  
        setIsTyping?.(false);
      }
    }, 5);
        
    return () => clearInterval(intervalId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [msg, resumeTyping]);

  const components = {
    // Remove custom table components - let CSS handle everything via className
    table: ({node, ...props}: any) => <table {...props} className="markdown-table" />,
    thead: ({node, ...props}: any) => <thead {...props} />,
    tbody: ({node, ...props}: any) => <tbody {...props} />,
    tr: ({node, ...props}: any) => <tr {...props} />,
    th: ({node, ...props}: any) => <th {...props} />,
    td: ({node, ...props}: any) => <td {...props} />,
    h2: ({node, children, ...props}: any) => <h2 className="center-align" {...props}>{children}</h2>,
    h3: ({node, children, ...props}: any) => <h3 className="center-align" {...props}>{children}</h3>,
    a: ({node, ...props}: any) => {
      //console.log("props.href: ", props.href);
      if (props && props.href.startsWith('/icon:')) {
        const iconType = props.href.split(':')[1];
        //console.log("iconType: ", iconType);
        return <img width="16px" height="16px" src={`/icons/${iconType}.svg`} alt={`${iconType} icon`} />;
      }
      // Render mailto links as plain text instead of clickable links
      if (props && props.href.startsWith('mailto:')) {
        return <span>{props.children}</span>;
      }
      return <a target="_blank" rel="noreferrer" href={props.href}>{props.children}</a>;
    },
    code: ({node, inline, className, children, ...props}: any) => {
      // Check if inline is explicitly true or if there's no className (inline code usually has no language class)
      const isInline = inline === true || (!className && typeof children === 'string' && children.length < 100);

      // For inline code, render normally
      if (isInline) {
        return <code className={className} {...props}>{children}</code>;
      }

      // For code blocks, try to detect and format JSON
      const codeString = String(children).trim();

      // Check if it looks like JSON (starts with { or [)
      if ((codeString.startsWith('{') || codeString.startsWith('[')) &&
          (codeString.endsWith('}') || codeString.endsWith(']'))) {
        try {
          const jsonData = JSON.parse(codeString);

          // Handle URLs array specifically
          if (jsonData.urls && Array.isArray(jsonData.urls)) {
            return (
              <ul style={{ listStyleType: 'disc', paddingLeft: '20px', margin: '10px 0', maxWidth: '100%' }}>
                {jsonData.urls.map((url: string, idx: number) => (
                  <li key={idx} style={{
                    marginBottom: '5px',
                    wordBreak: 'break-all',
                    overflowWrap: 'break-word'
                  }}>
                    <a href={url} target="_blank" rel="noopener noreferrer"
                       style={{
                         color: '#4f7d9c',
                         textDecoration: 'underline',
                         wordBreak: 'break-all',
                         overflowWrap: 'break-word'
                       }}>
                      {url}
                    </a>
                  </li>
                ))}
              </ul>
            );
          }

          // For other JSON, render as formatted pre
          return (
            <pre style={{
              backgroundColor: 'rgba(0,0,0,0.05)',
              padding: '10px',
              borderRadius: '4px',
              overflow: 'auto'
            }}>
              <code>{JSON.stringify(jsonData, null, 2)}</code>
            </pre>
          );
        } catch (e) {
          // If JSON parsing fails, render as normal code block
          return <code className={className} {...props}>{children}</code>;
        }
      }

      // For non-JSON code blocks, render normally
      return <code className={className} {...props}>{children}</code>;
    },
  };

  const getDeepestLastChild = (element: HTMLElement) => {
    let lastChild = element.lastElementChild;
  
    while (lastChild && lastChild.lastElementChild) {
      lastChild = lastChild.lastElementChild;
    }  
    return lastChild;
  };

  return (
    (loading)?
      <Cursor/> :    
      <div className='content' ref={contentRef}>
        <ReactMarkdown
          remarkPlugins={[remarkGfm]}
          components={components}
        >
          {displayResponse}
        </ReactMarkdown>
        {displayResponse === "" && <Cursor/>}
        {/* {!completedTyping && <Cursor/>} */}
        
        {/* Conditional rendering based on txt, pic, and vid props */}    
        {/*completedTyping && txt && <p>{txt}</p>*/}
        {completedTyping && pic && (pic !== "nan") && <p><img src={pic} alt="This is a picture!" /></p>}
        {completedTyping && vid && (vid !== "nan") && (
          <p>
            <video src={vid} controls>
            Your browser does not support the video tag.
            </video>
          </p>
        )}
        {renderCustom && custom &&
          <React.Fragment key="custom-component">
            {custom()}
          </React.Fragment>
        }
      </div>
  );

};

export default ShowTyping;